import { Device } from '@ynomia/core/dist/device';
import { createSelector } from 'reselect';
import dayjs from 'dayjs';
import some from 'lodash/some';
import isEmpty from 'lodash/isEmpty';
import {
  Asset,
  AssetStatusFilter,
  AssetTableColumn,
  RawObservationRecord,
  SYSTEM_TYPE,
} from '../../config/types';
import {
  checkIsFilteredBySelectedBandIds,
  checkIsFilteredByDate,
  checkIsFilteredByLayer,
  checkIsFilteredByObservationType,
  checkIsFilteredByStatus,
  checkIsFilteredByTable,
  checkSearchFilter,
  getDataSourceAssetFromAsset,
  getObservationSameOrBeforeTimeTravelDate,
  getSelectedLayerAncestors,
  buildAssetSearchContext,
  checkIsFilteredByFields,
  getEffectiveLayerIDs,
  getPlanResultFields,
  getSearchableStringFromDataSourceAsset,
} from '../../utils';
import {
  getAssetManagerAssetSelection,
  getAssetManagerFilters,
  getAssetManagerSelectedAssetTypeId,
  getAssetManagerSelectedPlanId,
  getAssetManagerShowPlanView,
  getAssetStatusesKeyedById,
  getAssetsArray,
  getBandsKeyedById,
  getCustomColumns,
  getDefaultAssetStatus,
  getLayersKeyedByAncestorId,
  getLinkedDevicesKeyedByAssetId,
  getListViewColumns,
  getLocationSummarySlotConfig,
  getPlannedTwinIDsKeyedByTwinId,
  getProjectUserListKeyedById,
  getSelectedAssetTypeFields,
} from '..';
import { AssetFilter } from '../../context';
import { assetArrayMemoizeOptions, assetFilterIsActive } from '../../utils/assetFilters';
import map from '../map';
import { UNKNOWN_BAND_ID } from '../../config/constants';

const getTimeTravelAssetsArray = createSelector(
  [
    getAssetManagerFilters,
    getAssetsArray,
    getDefaultAssetStatus,
  ],
  (
    assetManagerFilters,
    assetsArray,
    defaultStatus,
  ) => {
    const { timeTravelDate } = assetManagerFilters || {};
    if (!timeTravelDate) return assetsArray;
    // If the default status (usually "Added to Ynomia") is in the filter, allow all assets
    return assetsArray.map((asset: Asset) => {
      const newAsset = { ...asset } as Asset;
      const filteredObservations: RawObservationRecord[] = getObservationSameOrBeforeTimeTravelDate(
        asset,
        timeTravelDate,
      );

      const filteredObservationsStatusUpdate = filteredObservations.filter(
        ({ type }) => type === SYSTEM_TYPE.AUTOMATIC_STATUS_UPDATE_OBSERVATION
          || type === SYSTEM_TYPE.STATUS_UPDATE_OBSERVATION,
      );

      newAsset.observations = filteredObservations;

      if (filteredObservationsStatusUpdate[0]?.to) {
        const lastStatusUpdate = filteredObservationsStatusUpdate.find(
          ({ isOutOfSequence }) => !isOutOfSequence,
        ) || filteredObservationsStatusUpdate[0];
        newAsset.status = lastStatusUpdate.to;

        newAsset.statusUpdatedDate = dayjs(lastStatusUpdate.date).toDate();
      } else {
        newAsset.status = defaultStatus?.id;
        newAsset.statusUpdatedDate = dayjs(asset.created).toDate();
      }

      return newAsset;
    });
  },
  assetArrayMemoizeOptions,
);

export const getTimeTravelAssetsById = createSelector(
  [getTimeTravelAssetsArray],
  timeTravelAssetsArray => new Map(
    timeTravelAssetsArray.map(asset => [asset.id, asset]),
  ) as Map<string, Asset>,
);

export const getTimeTravelAssetsByTwinId = createSelector(
  [getTimeTravelAssetsById],
  (timeTravelAssetsById) => {
    const assetArray = [...timeTravelAssetsById.values()];
    return new Map(assetArray.map((asset) => {
      const [twinId] = asset.destination?.twinID || '';
      return [twinId, asset];
    })) as Map<string, Asset>;
  },
);

const getObservationTypesKeyedByAssetId = createSelector(
  [getTimeTravelAssetsArray],
  assetsArray => new Map(
    assetsArray.map(({ id, observations }) => [id, observations?.map(({ type }) => type)]),
  ),
);

export const getSelectedAssets = createSelector(
  [
    getAssetManagerAssetSelection,
    getTimeTravelAssetsById,
  ],
  (assetManagerAssetSelection, assetsKeyedById) => {
    const { selectedAssets } = assetManagerAssetSelection;
    return selectedAssets.map(selected => assetsKeyedById.get(selected)) as Array<Asset>;
  },
);

export const getTimeTravelAssetsKeyedByType = createSelector(
  [map.assetTypes, getTimeTravelAssetsArray],
  (assetTypes, assetArray) => {
    const assetsKeyedByTypes = new Map<string, Array<Asset>>();
    if (assetTypes && assetArray) {
      assetTypes.forEach(({ id }) => {
        const assetsFilteredByStatusId = assetArray.filter(({ type }) => type === id);
        assetsKeyedByTypes.set(id, assetsFilteredByStatusId);
      });
    }

    return assetsKeyedByTypes as Map<string, Array<Asset>>;
  },
);

export const getAssetsArrayFromAssetType = createSelector(
  [getAssetManagerSelectedAssetTypeId, getTimeTravelAssetsKeyedByType, getTimeTravelAssetsArray],
  (selectedAssetTypeId, assetsKeyedByTypes, assetsArray) => (
    assetsKeyedByTypes.get(selectedAssetTypeId) || assetsArray
  ),
  assetArrayMemoizeOptions,
);

export const getPlansKeyedByAssetId = createSelector(
  [
    getAssetsArray,
    getPlannedTwinIDsKeyedByTwinId,
    getBandsKeyedById,
    getAssetManagerSelectedPlanId,
  ],
  (
    assetArray,
    plannedTwinIdsKeyedByTwinId,
    bandsKeyedById,
    selectedPlanId,
  ) => {
    const planKeyedByAssetId = new Map<string, any>(assetArray.map(asset => [asset.id, asset]));

    assetArray.forEach(({ id, destination }) => {
      const twinID = destination?.twinID?.[0] || null;
      const plannedTwinIds = plannedTwinIdsKeyedByTwinId
        ?.get(twinID!)
        ?.find(plannedTwinId => plannedTwinId.planID === selectedPlanId);
      const { result } = plannedTwinIds || {};
      const { bandID, nextPlannedStatus } = getPlanResultFields(result);
      const band = bandsKeyedById.get(bandID || UNKNOWN_BAND_ID);
      const planStatus = band?.label;
      const bandColor = band?.color;
      const bandDanger = band?.danger;
      const { minDaysUntilDueDateThreshold } = band || {};
      const plannedDueDate = nextPlannedStatus?.dueDate;
      const daysUntilDueDate = nextPlannedStatus?.daysUntilDueDate;
      const plannedStatusId = nextPlannedStatus?.id;

      planKeyedByAssetId.set(id, {
        planStatus,
        bandColor,
        bandDanger,
        minDaysUntilDueDateThreshold,
        plannedDueDate,
        daysUntilDueDate,
        plannedStatusId,
      });
    });
    return planKeyedByAssetId;
  },
);

/**
 * getDataSourceKeyedByAssetId performs some heavy operations per asset,
 * hence why we cache the computed result for each asset.
 * `customColumnsRef` and `linkedDevicesKeyedByAssetIdRef` are used as part of
 * a custom selector which helps us determine when to recalculate all the data.
 */
const dataSourceAssetCache = new Map<Asset, any>();
let customColumnsRef: AssetTableColumn[];
let plannedTwinIdsKeyedByTwinIdRef: Map<string, any> | null = null;
let timeTravelAssetsArrayRef: Array<Asset>;
let linkedDevicesKeyedByAssetIdRef: Map<string | undefined, Device>;

export const getDataSourceKeyedByAssetId = createSelector(
  [
    map.layersKeyedById,
    getTimeTravelAssetsById,
    getCustomColumns,
    getAssetsArrayFromAssetType,
    getAssetStatusesKeyedById,
    getLinkedDevicesKeyedByAssetId,
    getTimeTravelAssetsArray,
    getPlannedTwinIDsKeyedByTwinId,
    getBandsKeyedById,
    getAssetManagerSelectedPlanId,
    getProjectUserListKeyedById,
  ],
  (
    layersKeyedById,
    assetsKeyedById,
    customColumns,
    assetsArray,
    assetStatusesKeyedById,
    linkedDevicesKeyedByAssetId,
    timeTravelAssetsArray,
    plannedTwinIdsKeyedByTwinId,
    bandsKeyedById,
    selectedPlanId,
    projectUserListKeyedById,
  ): Map<string, ReturnType<typeof getDataSourceAssetFromAsset>> => {
    /**
     * Recompute all assets when custom columns, linked devices or planned twin IDs change.
     * They are data external from the assets collection which affect the list view datasource.
     */
    if (
      customColumns !== customColumnsRef
      || (timeTravelAssetsArray !== timeTravelAssetsArrayRef)) {
      dataSourceAssetCache.clear();
      customColumnsRef = customColumns;
      timeTravelAssetsArrayRef = timeTravelAssetsArray;
    }

    if (linkedDevicesKeyedByAssetId !== linkedDevicesKeyedByAssetIdRef) {
      dataSourceAssetCache.clear();
      linkedDevicesKeyedByAssetIdRef = linkedDevicesKeyedByAssetId;
    }

    if (plannedTwinIdsKeyedByTwinId !== plannedTwinIdsKeyedByTwinIdRef) {
      dataSourceAssetCache.clear();
      plannedTwinIdsKeyedByTwinIdRef = plannedTwinIdsKeyedByTwinId;
    }

    return new Map(assetsArray.map(
      (asset: Asset) => {
        const cacheHit = dataSourceAssetCache.get(asset);
        if (cacheHit) return [asset.id, cacheHit];
        const data = getDataSourceAssetFromAsset(
          asset,
          customColumns,
          layersKeyedById,
          assetsKeyedById,
          assetStatusesKeyedById,
          linkedDevicesKeyedByAssetId,
          plannedTwinIdsKeyedByTwinId,
          bandsKeyedById,
          projectUserListKeyedById,
          selectedPlanId,
        );

        dataSourceAssetCache.set(asset, data);
        return [asset.id, data];
      },
    ));
  },
);

/**
 * Returns a cached searchable-string getter for data source assets. The cache is
 * keyed by data source asset identity (rows are themselves cached per asset in
 * getDataSourceKeyedByAssetId), and is rebuilt whenever anything the searchable
 * string derives from changes.
 */
export const getSearchableStringForDataSourceAsset = createSelector(
  [
    map.layersKeyedById,
    getListViewColumns,
    getSelectedAssetTypeFields,
    map.timezone,
    getAssetManagerShowPlanView,
  ],
  (layersKeyedById, listViewColumns, assetTypeFields, timezone, showPlanView) => {
    const cache = new WeakMap<Record<string, any>, string>();
    const context = buildAssetSearchContext(
      listViewColumns,
      assetTypeFields,
      layersKeyedById,
      timezone,
      showPlanView ?? false,
    );
    return (dataSourceAsset: Record<string, any>): string => {
      const cached = cache.get(dataSourceAsset);
      if (cached !== undefined) return cached;
      const searchableString = getSearchableStringFromDataSourceAsset(dataSourceAsset, context);
      cache.set(dataSourceAsset, searchableString);
      return searchableString;
    };
  },
);

export const getFilteredAssetsContext = createSelector(
  [
    map.layersKeyedById,
    getLinkedDevicesKeyedByAssetId,
    getAssetsArrayFromAssetType,
    getAssetStatusesKeyedById,
    getListViewColumns,
    getLayersKeyedByAncestorId,
    getObservationTypesKeyedByAssetId,
    getDataSourceKeyedByAssetId,
    getPlannedTwinIDsKeyedByTwinId,
    map.assetsKeyedById,
    getLocationSummarySlotConfig,
    getSearchableStringForDataSourceAsset,
  ],
  (
    layersKeyedById,
    linkedDevicesKeyedByAssetId,
    assetsArrayFromAssetType,
    assetStatusesKeyedById,
    listViewColumns,
    layersKeyedByAncestorId,
    observationTypesKeyedByAssetId,
    listViewDataSource,
    plannedTwinIdsKeyedByTwinId,
    assetsKeyedById,
    locationSummarySlotConfig,
    getSearchableString,
  ) => ({
    layersKeyedById,
    linkedDevicesKeyedByAssetId,
    assetsArrayFromAssetType,
    assetStatusesKeyedById,
    listViewColumns,
    layersKeyedByAncestorId,
    observationTypesKeyedByAssetId,
    listViewDataSource,
    plannedTwinIdsKeyedByTwinId,
    assetsKeyedById,
    locationSummarySlotConfig,
    getSearchableString,
  }),
);

export const getFilteredAssets = (
  {
    layersKeyedById,
    linkedDevicesKeyedByAssetId,
    assetsArrayFromAssetType,
    assetStatusesKeyedById,
    listViewColumns,
    layersKeyedByAncestorId,
    observationTypesKeyedByAssetId,
    listViewDataSource,
    plannedTwinIdsKeyedByTwinId,
    assetsKeyedById,
    locationSummarySlotConfig,
    getSearchableString,
  }: ReturnType<typeof getFilteredAssetsContext>,
  {
    searchTags,
    searchText,
    dateFilter,
    assetStatusFilter,
    observationTypeFilter = {},
    tableFilter,
    selectedBandIds,
    openedLayerId,
    fieldFilters,
    multiSearchModeFilter,
    selectedLayerIds,
  }: Partial<AssetFilter> & { selectedAssetTypeId?: string },
): Array<Asset> => {
  const defaultStatus = [...assetStatusesKeyedById.values()].find(s => s.default);
  const observationTypeFilterIds = Object.keys(observationTypeFilter).length || 0;
  const includeUnlocated = selectedLayerIds?.includes(null) ?? false;
  const selectedLayerAncestors = getSelectedLayerAncestors(
    selectedLayerIds || [],
    layersKeyedByAncestorId,
  );

  const { slots: locationSlots, fieldFilter: configFieldFilter } = locationSummarySlotConfig;
  // The location summary's config fieldFilter is part of the location
  // selection, so it must apply whenever the user is scoped to a layer:
  // either by selecting bars (selectedLayerIds) or by drilling into one
  // (openedLayerId). Otherwise the bars count fewer assets than the list,
  // twin and other charts show for the same layer.
  const hasLocationFilter = (!!selectedLayerIds && selectedLayerIds.length > 0)
    || !!openedLayerId;
  const effectiveFieldFilters = {
    ...(hasLocationFilter ? configFieldFilter : {}),
    ...(fieldFilters ?? {}),
  };

  const tableFilterExists = some(tableFilter, value => !!value?.length);
  const searchFilter = (searchTags?.length || searchText)
    ? checkSearchFilter(searchTags || [], searchText, multiSearchModeFilter)
    : null;

  const filteredAssets = assetsArrayFromAssetType
    .filter((asset: Asset) => {
      const effectiveLayerIDs = getEffectiveLayerIDs(
        asset,
        locationSlots,
        assetsKeyedById as Map<string, Asset>,
      );

      if (selectedLayerAncestors.size || includeUnlocated) {
        const matchesLayer = [...effectiveLayerIDs].some(id => selectedLayerAncestors.has(id));
        const matchesUnlocated = includeUnlocated && effectiveLayerIDs.size === 0;
        if (!matchesLayer && !matchesUnlocated) return false;
      }

      if (assetStatusFilter?.ids?.length && !checkIsFilteredByStatus(
        asset,
        assetStatusesKeyedById,
        assetStatusFilter as AssetStatusFilter,
        defaultStatus,
      )) return false;

      if (openedLayerId
        && ![...effectiveLayerIDs].some(id => checkIsFilteredByLayer(
          openedLayerId,
          layersKeyedById,
          id,
          layersKeyedByAncestorId,
        ))) return false;

      if (dateFilter?.length === 2
        && !checkIsFilteredByDate(asset, dateFilter, assetStatusFilter)) return false;

      if (observationTypeFilterIds
        && !checkIsFilteredByObservationType(
          asset,
          observationTypeFilter,
          observationTypesKeyedByAssetId,
        )) return false;

      if (selectedBandIds && Object.keys(selectedBandIds).length
        && !checkIsFilteredBySelectedBandIds(
          asset,
          plannedTwinIdsKeyedByTwinId,
          selectedBandIds,
        )) return false;

      const dataSourceAsset = listViewDataSource.get(asset.id) || {};

      if (tableFilterExists
        && !checkIsFilteredByTable(
          asset,
          linkedDevicesKeyedByAssetId,
          tableFilter,
          listViewColumns,
          dataSourceAsset,
        )) return false;

      if (
        !isEmpty(effectiveFieldFilters)
        && !checkIsFilteredByFields(asset, effectiveFieldFilters)
      ) return false;

      if (searchFilter && !searchFilter(getSearchableString(dataSourceAsset))) return false;

      return true;
    });
  return filteredAssets;
};

export const getActiveAssetFilters = createSelector(
  [getAssetManagerFilters],
  assetFilterIsActive,
);
